/**
 * Analytics.tsx — Cloudflare Web Analytics + Microsoft Clarity (React).
 *
 * Carrega SEMPRE, sem consentimento necessário.
 * CF Web Analytics: zero cookies, zero PII — privacy-first by design.
 * Microsoft Clarity: dados anônimos de sessão, sem identificação direta.
 *
 * Uso: adicionar <Analytics /> no root do App.tsx, antes de qualquer conteúdo.
 * Carregamento acontece uma única vez via useEffect com array de deps vazio.
 */
import { useEffect } from 'react';

/** Estende Window para evitar erros de TypeScript com scripts globais */
declare global {
  interface Window {
    clarity?: (...args: unknown[]) => void;
    __analytics_loaded?: boolean;
  }
}

export function Analytics(): null {
  useEffect(() => {
    /* Guarda flag para evitar dupla injeção em re-renders (StrictMode dev) */
    if (window.__analytics_loaded) return;
    window.__analytics_loaded = true;

    const cfToken = import.meta.env.VITE_CF_ANALYTICS_TOKEN ?? '';
    const clarityId = import.meta.env.VITE_CLARITY_PROJECT_ID ?? '';

    /* ── Cloudflare Web Analytics ── */
    if (cfToken) {
      const script = document.createElement('script');
      script.defer = true;
      script.src = 'https://static.cloudflareinsights.com/beacon.min.js';
      script.setAttribute('data-cf-beacon', JSON.stringify({ token: cfToken }));
      document.head.appendChild(script);
    }

    /* ── Microsoft Clarity ── */
    if (clarityId) {
      /* Inicializa a função clarity antes do script carregar (pattern oficial) */
      window.clarity = window.clarity || function(...args: unknown[]) {
        // @ts-expect-error — pattern de fila pré-carregamento do Clarity
        (window.clarity!.q = window.clarity!.q || []).push(args);
      };

      const script = document.createElement('script');
      script.async = true;
      script.src = `https://www.clarity.ms/tag/${clarityId}`;
      document.head.appendChild(script);
    }
  }, []);

  /* Componente invisível — sem renderização de DOM */
  return null;
}
